import { Injectable } from '@angular/core';
import { ISO8601DateString } from '@portal-core/data/common/types/ISO-date-string.type';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

/**
 * One emission from `LogStreamService.getStream$`. Tagged on `kind` so consumers can `switch`
 * once and get exhaustive coverage of everything that can arrive on the SSE wire, plus the
 * two non-content lifecycle markers (`open` / `done`) that callers usually need to surface in
 * the UI.
 *
 * - `open`: the EventSource handshake completed. Useful for dropping a loading placeholder
 *   before the first real entry arrives.
 * - `log`: a `k="l"` envelope from the writer — a compiler or runtime log line. The payload
 *   is the wire shape `{sev, msg, t, src, srcLine, errorId, subsystem}` (see `LogPayload`).
 * - `event`: a `k="e"` envelope — a process state event. `name` is the camel-cased event
 *   name (`"buildStart"`, `"compileComplete"`, …); `data` holds the event payload.
 * - `raw`: an SSE line the upstream parser couldn't decode as JSON. Forwarded verbatim so
 *   nothing is silently dropped; the live-log surfaces these as info-severity log lines.
 * - `done`: the source's terminal `done` event. The Observable completes immediately after,
 *   so consumers using `complete` can also detect end-of-stream without watching for this.
 */
export type LogStreamMessage =
  | { kind: 'open' }
  | { kind: 'log'; data: LogPayload }
  | { kind: 'event'; name: string; data: LogEventPayload }
  | { kind: 'raw'; text: string }
  | { kind: 'done' };

/**
 * Shared JSON shape for both log entries (`k="l"`) and event payloads (`k="e"`), mirroring the
 * server's `LiveLogPayloadBase`. Add fields here (not to each subtype) when the server adds one
 * to its base class.
 */
export interface LiveLogPayloadBase {
  /** Severity, e.g. 'info' | 'warn' | 'error' | 'debug'. */
  sev?: string;
  msg?: string;
  /** Server: `LiveLogPayloadBase.Timestamp`, `DateTime.ToString("o")` — not epoch millis or a Date. */
  t?: ISO8601DateString;
}

export interface LogPayload extends LiveLogPayloadBase {
  src?: string;
  srcLine?: number;
  errorId?: string;
  subsystem?: string;
}

export interface LogEventPayload extends LiveLogPayloadBase {
  /** Normalized [0, 1] progress on `n="progress"` envelopes (server: `LiveLogEvent.Progress`). */
  progress?: number;
}

interface CompletionFlag {
  completed: boolean;
}

interface CacheEntry {
  stream$: Observable<LogStreamMessage>;
  flag: CompletionFlag;
}

/**
 * Multicasts a live-log SSE stream so concurrent consumers (the Log tab's `mc-live-log` and the
 * feature-specific grid) share one EventSource per stream URL instead of each opening its own.
 * Late subscribers get everything previously emitted via `shareReplay`'s buffer.
 *
 * The cache is keyed by stream URL; callers (build-profile-dialog, publication-profile-dialog)
 * resolve the URL via their feature-specific API service and hand it in.
 *
 * Eviction has two triggers, because completing a subscription always auto-unsubscribes it
 * (collapsing every current subscriber to zero the instant the stream finishes) — there's no
 * RxJS-native way to tell "still on an open tab" apart from "genuinely done":
 * - An INCOMPLETE stream self-evicts once it has no subscribers (normal refCount teardown).
 * - A COMPLETED stream is deliberately NOT auto-evicted (a sibling tab opened after the build
 *   finished still needs to find it) — only `release()` removes it, called by the owning
 *   dialog's `ngOnDestroy`. Retention is scoped to that dialog's lifetime, not a cap or timer.
 */
@Injectable({ providedIn: 'root' })
export class LogStreamService {
  private cache = new Map<string, CacheEntry>();

  getStream$(streamUrl: string): Observable<LogStreamMessage> {
    const existing = this.cache.get(streamUrl);
    if (existing) {
      return existing.stream$;
    }

    const flag: CompletionFlag = { completed: false };
    const stream$ = this.openStream(streamUrl, flag).pipe(
      // bufferSize: Infinity so a tab joining mid-stream replays everything from the start.
      // Memory cost is bounded by one stream's entry count (never accumulates across streams —
      // see the class doc comment) — fine for typical scale; future "very large logs" work
      // will revisit when we hit it.
      shareReplay({ bufferSize: Number.POSITIVE_INFINITY, refCount: true })
    );
    this.cache.set(streamUrl, { stream$, flag });
    return stream$;
  }

  /**
   * Drops the cached stream for `streamUrl`, if any. Call from the owning dialog's
   * `ngOnDestroy` — a completed stream is otherwise never removed. No-op if nothing's cached.
   */
  release(streamUrl: string) {
    this.cache.delete(streamUrl);
  }

  private openStream(streamUrl: string, flag: CompletionFlag): Observable<LogStreamMessage> {
    return new Observable<LogStreamMessage>(subscriber => {
      const eventSource = new EventSource(streamUrl, { withCredentials: true });

      eventSource.onopen = () => subscriber.next({ kind: 'open' });

      eventSource.onmessage = (event: MessageEvent) => {
        const msg = parseEnvelope(event.data);
        if (msg) {
          subscriber.next(msg);
        }
      };

      eventSource.addEventListener('done', () => {
        subscriber.next({ kind: 'done' });
        flag.completed = true;
        subscriber.complete();
        eventSource.close();
      });

      return () => {
        eventSource.close();
        // Only self-evict if incomplete (see class doc comment); compare by `flag` identity so
        // a stale teardown can't delete a newer generation's entry for the same URL.
        if (!flag.completed && this.cache.get(streamUrl)?.flag === flag) {
          this.cache.delete(streamUrl);
        }
      };
    });
  }
}

function parseEnvelope(raw: string): LogStreamMessage | null {
  let envelope: { k?: string; n?: string; d?: LogPayload | LogEventPayload };
  try {
    envelope = JSON.parse(raw);
  } catch {
    return { kind: 'raw', text: raw };
  }
  if (envelope.k === 'l') {
    return { kind: 'log', data: (envelope.d as LogPayload) ?? {} };
  }
  if (envelope.k === 'e') {
    return { kind: 'event', name: envelope.n ?? '', data: (envelope.d as LogEventPayload) ?? {} };
  }
  return null;
}
